/**
 * Google's own "Sign in with Google" button.
 *
 * Google renders the button and runs the sign-in in its own popup, then hands
 * back an ID token, which goes to the gateway to check. Using Google's button
 * rather than drawing one is deliberate: it is the version Google keeps
 * working as browsers change how third-party sign-in may behave, and it is
 * the one people recognise and trust.
 *
 * The login page renders it only when the gateway says Google sign-in is set
 * up, and passes the client ID the gateway gave it.
 */
import { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

interface GoogleIdApi {
  initialize(options: {
    client_id: string;
    callback: (response: { credential?: string }) => void;
    ux_mode?: 'popup' | 'redirect';
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }): void;
  renderButton(
    parent: HTMLElement,
    options: {
      type?: 'standard' | 'icon';
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      size?: 'large' | 'medium' | 'small';
      text?: 'signin_with' | 'continue_with';
      shape?: 'rectangular' | 'pill';
      logo_alignment?: 'left' | 'center';
      width?: number;
    },
  ): void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleIdApi } };
  }
}

let scriptPromise: Promise<void> | null = null;

/** Load Google's script once, however many times the button mounts. */
function loadScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  scriptPromise ??= new Promise<void>((resolve, reject) => {
    const el = document.createElement('script');
    el.src = SCRIPT_SRC;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => {
      scriptPromise = null;
      reject(new Error('Could not load Google sign-in'));
    };
    document.head.appendChild(el);
  });
  return scriptPromise;
}

export function GoogleButton({
  clientId,
  width,
  onCredential,
  onError,
}: {
  clientId: string;
  width: number;
  /** Called with the ID token once someone picks their Google account. */
  onCredential: (credential: string) => void;
  onError: (message: string) => void;
}) {
  const slot = useRef<HTMLDivElement>(null);
  // Latest callbacks, so Google's one-time initialise never calls a stale one.
  const handlers = useRef({ onCredential, onError });
  handlers.current = { onCredential, onError };

  useEffect(() => {
    if (!slot.current) return;
    let live = true;
    loadScript()
      .then(() => {
        const id = window.google?.accounts.id;
        if (!live || !id || !slot.current) return;
        id.initialize({
          client_id: clientId,
          ux_mode: 'popup',
          auto_select: false,
          callback: (res) => {
            if (res.credential) handlers.current.onCredential(res.credential);
            else handlers.current.onError('Google did not complete the sign-in');
          },
        });
        id.renderButton(slot.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          width,
        });
      })
      .catch((e: unknown) => {
        if (live) handlers.current.onError(e instanceof Error ? e.message : 'Could not load Google sign-in');
      });
    return () => {
      live = false;
    };
  }, [clientId, width]);

  // Google sizes its own button; this just reserves the row so the form below
  // does not jump when it appears.
  return <div ref={slot} style={{ minHeight: 44, display: 'flex', justifyContent: 'center' }} />;
}
